import { Alert, Space, Spin } from 'antd';
import * as React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';

import { CompanyModal } from '../../components/Alerts/CompanyModal';
import { IdentityModal } from '../../components/Alerts/IdentityModal';
import { ToastVariant } from '../../components/Toast/toast.model';
import { toastService } from '../../components/Toast/toast.service';
import { firebaseFunctions } from '../../firebase/firebase';
import { FirebaseCallableFunctions } from '../../firebase/firebase.models';
import { useGetProjectOffer } from '../../firebase/hooks/getProjectOfferById.hook';
import { Offer, OfferStatusState } from '../../models/offers.model';
import { App, SHOW_LOGS } from '../App/App';
import { handleCampaign, useAuth } from '../Auth/AuthContext';
import { AppRoutes } from '../Routing/routing.model';

import { HowItWorks } from './components/HowItWorks';
import { OfferForm } from './components/OfferForm';
import { handleOnOfferAdd } from './hooks/handleOnOfferAdd.hook';
import useSessionTracker from './hooks/useSessionTracker.hook';
import { ImageVerificationModal } from './ImageVerificationModal';

interface Props {
  offerId: string;
}

interface PropsWhenAuth {
  offerId: string;
  initial: Offer;
  error: string;
  currentUser: any;
  hasPermission: boolean;
}

export const OfferAdd: React.FC<Props> = ({ offerId }) => {
  const { currentUser } = useAuth();
  const { offer, error, hasPermission } = useGetProjectOffer(offerId);
  useSessionTracker(offer?.id);

  const callLinkProjectOffer = async () => {
    try {
      const campaignData = handleCampaign();
      if (!campaignData) return;

      const callableFunction = firebaseFunctions.httpsCallable(FirebaseCallableFunctions.ON_CLICK_OFFER_ADD_LINK);
      const resp = await callableFunction(campaignData);

      SHOW_LOGS && console.log(resp);
    } catch (error) {
      console.error('Error in callLinkProjectOffer:', error);
    }
  };

  React.useEffect(() => {
    callLinkProjectOffer();
  }, []);

  return (
    <App>
      <WhenAuth
        offerId={offerId}
        initial={offer}
        error={error}
        currentUser={currentUser}
        hasPermission={hasPermission}
      />
    </App>
  );
};

const WhenAuth: React.FC<PropsWhenAuth> = ({ offerId, initial, error, currentUser, hasPermission }) => {
  const { push } = useHistory();
  const [loading, setLoading] = useState<boolean>();
  const [isIdentityModalOpen, setIsIdentityModalOpen] = useState(false);
  const [isCompanyModalOpen, setIsCompanyModalOpen] = useState(false);
  const [imagesVerificationResults, setImagesVerificationResults] = useState<string | null>(null);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const { t } = useTranslation();
  const [hasSubmitted, setHasSubmitted] = useState(false);

  // Używamy initial jako offer
  const offer = initial;

  console.log('DEBUG: offer', offer);

  const onOfferAdd = async () => {
    if (hasSubmitted) return;
    setHasSubmitted(true);
    const { success, description } = await handleOnOfferAdd(
      currentUser,
      offer,
      setIsIdentityModalOpen,
      setIsCompanyModalOpen,
      setLoading,
      setImagesVerificationResults,
      push
    );

    toastService.show(description, success ? t('SUCCESS') : t('ERROR_DESCRIPTION'), {
      variant: success ? ToastVariant.SUCCESS : ToastVariant.ERROR,
    });

    if (imagesVerificationResults) {
      setShowVerificationModal(true);
    }
  };

  const handleCloseModal = () => setShowVerificationModal(false);

  if (error) {
    return (
      <>
        <Space direction="vertical" style={{ width: '100%', marginTop: '100px', marginBottom: '60%', padding: '50px' }}>
          <Alert showIcon message={t('NO_ACCESS_TO_FORM')} description={error} type="error" />
        </Space>
      </>
    );
  } else if (initial === undefined || !offer) {
    return (
      <Space size="middle" className="absolute top-1/2 right-1/2">
        <Spin />
      </Space>
    );
  } else if (offer?.hasAgencyAgreement && currentUser && currentUser.uid === offer.uid) {
    // Agent with signed agreement can edit (ownership was transferred to agent)
    return (
      <>
        <OfferForm
          offerId={offerId}
          initial={initial}
          onAddOffers={onOfferAdd}
          isAddingPending={loading}
          hasPermission={true}
        />
        <IdentityModal isIdentityModalOpen={isIdentityModalOpen} />
        <CompanyModal isCompanyModalOpen={isCompanyModalOpen} />
        <ImageVerificationModal
          show={showVerificationModal}
          onClose={handleCloseModal}
          verificationResults={imagesVerificationResults}
          onChangeImage={() => push(AppRoutes.ADD_OFFER.replace(':id', offer.id))}
        />
      </>
    );
  } else if (offer?.hasAgencyAgreement) {
    // Anyone else (including logged in/out users) cannot edit after agreement is signed
    return <WhenNoAccess reason="powerOfAttorney" agentUid={offer.agentUid} offerName={offer.name} />;
  } else if (offer?.offerStatus?.state !== OfferStatusState.UNSIGNED) {
    return <WhenNoAccess reason="signed" />;
  } else {
    return (
      <>
        <OfferForm
          offerId={offerId}
          initial={initial}
          onAddOffers={onOfferAdd}
          isAddingPending={loading}
          hasPermission={hasPermission}
        />
        <IdentityModal isIdentityModalOpen={isIdentityModalOpen} />
        <CompanyModal isCompanyModalOpen={isCompanyModalOpen} />
        <ImageVerificationModal
          show={showVerificationModal}
          onClose={handleCloseModal}
          verificationResults={imagesVerificationResults}
          onChangeImage={() => push(AppRoutes.ADD_OFFER.replace(':id', offer.id))}
        />
        <HowItWorks />
      </>
    );
  }
};

interface WhenNoAccessProps {
  reason: 'signed' | 'powerOfAttorney';
  agentUid?: string;
  offerName?: string;
}

const WhenNoAccess: React.FC<WhenNoAccessProps> = ({ reason, agentUid, offerName }) => {
  const { t } = useTranslation();

  if (reason === 'powerOfAttorney') {
    return (
      <Space direction="vertical" style={{ width: '100%', marginTop: '100px', marginBottom: '60%', padding: '50px' }}>
        <Alert
          showIcon
          message="Pełnomocnictwo zostało udzielone"
          description={
            <div>
              <p>
                Ta nieruchomość ({offerName || 'bez nazwy'}) jest obecnie zarządzana przez agenta na podstawie
                udzielonego pełnomocnictwa.
              </p>
              <p>Agent przejął kontrolę nad ogłoszeniem i jest odpowiedzialny za jego prowadzenie.</p>
              {agentUid && <p>ID agenta: {agentUid}</p>}
              <p>W razie pytań skontaktuj się z agentem lub obsługą platformy.</p>
            </div>
          }
          type="info"
        />
      </Space>
    );
  }

  // Original signed offer message
  return (
    <>
      <Space direction="vertical" style={{ width: '100%', marginTop: '100px', marginBottom: '60%', padding: '50px' }}>
        <Alert showIcon message={t('ERROR')} description={t('OFFER_NOT_EDITABLE')} type="error" />
      </Space>
    </>
  );
};
